import Seo, { breadcrumb } from "@/components/Seo";
import { CoveButton } from "@/components/CoveButton";
import { Reveal, FloatingBlob } from "@/components/motion";
import { Sparkles, Star } from "lucide-react";
import { SITE, IMAGES, ABOUT } from "@/data/content";

const personSchema = {
  "@context": "https://schema.org",
  "@type": "Person",
  name: "Laura McLelland",
  jobTitle: "Occupational Therapist & Craniosacral Practitioner",
  worksFor: { "@type": "Organization", name: "Cranio Cove" },
  url: `${SITE.url}/about`,
  image: IMAGES.lauraCandid,
};

const ColHeading = ({ children }) => (
  <div className="flex items-center gap-3 justify-center lg:justify-start">
    <Sparkles size={16} className="text-cove-gold" aria-hidden="true" />
    <h2 className="text-sm tracking-[0.2em] uppercase text-cove-slate font-medium">{children}</h2>
  </div>
);

export default function AboutLaura() {
  return (
    <>
      <Seo
        title="About Laura McLelland"
        description="Meet Laura McLelland, Occupational Therapist and Craniosacral Practitioner at Cranio Cove in Tarrytown, NY. Her journey, training, certifications, and gentle philosophy of care."
        path="/about"
        image={IMAGES.lauraCandid}
        schema={personSchema}
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-cove-petal pt-32 md:pt-40 pb-16 md:pb-24 px-6 md:px-12" data-testid="about-hero">
        <FloatingBlob color="blush" size={420} className="-top-16 -left-24" delay={0.2} />
        <div className="relative mx-auto max-w-[1400px] grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <nav aria-label="Breadcrumb" className="mb-6">
              <ol className="flex items-center gap-2 text-xs tracking-[0.15em] uppercase text-cove-taupe">
                {breadcrumb([{ name: "Home", path: "/" }, { name: "About Laura", path: "/about" }]).itemListElement.map((c, i, arr) => (
                  <li key={i} className="flex items-center gap-2">
                    <span className={i === arr.length - 1 ? "text-cove-olive" : ""}>{c.name}</span>
                    {i < arr.length - 1 && <span className="text-cove-rose">/</span>}
                  </li>
                ))}
              </ol>
            </nav>
            <p className="text-sm tracking-[0.25em] uppercase text-cove-slate/70 mb-4 sr-only">About Laura</p>
            <h1 className="font-serif font-light text-5xl md:text-6xl lg:text-7xl leading-[0.95] text-cove-slate">Hi, I&rsquo;m Laura</h1>
            <p className="mt-5 text-base tracking-[0.2em] uppercase text-cove-olive">Welcome to Cranio Cove</p>
            <div className="mt-8 space-y-5 max-w-xl">
              {ABOUT.intro.map((p, i) => (
                <p key={i} className="text-lg leading-relaxed text-cove-olive">{p}</p>
              ))}
            </div>
            <p className="mt-8 font-script text-3xl md:text-4xl text-cove-rose">{ABOUT.script}</p>
            <div className="mt-8">
              <CoveButton calendly variant="primary" testId="about-book-button" className="tracking-[0.2em] uppercase text-xs px-10 py-4">
                Book Your Session
              </CoveButton>
            </div>
          </div>

          <Reveal>
            <div className="relative mx-auto max-w-md">
              <Sparkles className="absolute -top-2 right-2 text-cove-gold z-10" size={22} aria-hidden="true" />
              <Star className="absolute top-16 -right-2 text-cove-gold/70 z-10" size={14} aria-hidden="true" />
              <div className="overflow-hidden rounded-t-[180px] rounded-b-[2.5rem] shadow-[0_30px_70px_rgba(41,54,80,0.15)]">
                <img
                  src={IMAGES.lauraCandid}
                  alt="Laura McLelland, Craniosacral Practitioner, smiling warmly"
                  loading="lazy"
                  className="w-full h-[440px] md:h-[600px] object-cover object-[75%_35%]"
                />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Three columns */}
      <section className="px-6 md:px-12 py-20 md:py-28 bg-cove-petal" data-testid="about-columns">
        <div className="mx-auto max-w-[1400px] grid lg:grid-cols-3 gap-12 lg:gap-10 lg:divide-x lg:divide-cove-slate/10">
          <Reveal className="lg:pr-10">
            <ColHeading>{ABOUT.journeyHeading}</ColHeading>
            <div className="mt-6 space-y-4 text-cove-olive leading-relaxed text-sm">
              {ABOUT.journey.map((p, i) => <p key={i}>{p}</p>)}
            </div>
          </Reveal>

          <Reveal delay={0.1} className="lg:px-10">
            <ColHeading>My Training &amp; Certifications</ColHeading>
            <ul className="mt-6 space-y-5">
              {ABOUT.credentials.map((c, i) => (
                <li key={i} className="border-b border-cove-slate/10 pb-5 last:border-0">
                  <div className="flex items-start gap-2">
                    <Sparkles size={13} className="text-cove-gold mt-1 shrink-0" aria-hidden="true" />
                    <div>
                      <h3 className="font-serif text-xl text-cove-slate">{c.title}</h3>
                      {c.items.length === 1 ? (
                        <p className="mt-1 text-sm text-cove-olive">{c.items[0]}</p>
                      ) : (
                        <ul className="mt-2 space-y-1">
                          {c.items.map((it, j) => (
                            <li key={j} className="flex items-center gap-2 text-sm text-cove-olive">
                              <span className="h-1 w-1 rounded-full bg-cove-rose" />{it}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal delay={0.2} className="lg:pl-10">
            <ColHeading>My Philosophy</ColHeading>
            <div className="mt-6 rounded-[1.5rem] bg-cove-blush/30 p-7">
              <p className="font-serif italic text-2xl leading-snug text-cove-slate">&ldquo;{ABOUT.philosophyQuote}&rdquo;</p>
            </div>
            <div className="mt-6 space-y-4 text-cove-olive leading-relaxed text-sm">
              {ABOUT.philosophy.map((p, i) => <p key={i}>{p}</p>)}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Mission */}
      <section className="px-6 md:px-12 py-16 md:py-20 bg-gradient-to-b from-cove-restorative to-cove-blossom/40" data-testid="about-mission">
        <div className="mx-auto max-w-3xl text-center">
          <Reveal>
            <p className="text-xs tracking-[0.25em] uppercase text-cove-rose mb-4">My mission</p>
            <p className="font-serif text-2xl md:text-3xl leading-snug text-cove-slate">
              {ABOUT.mission}
            </p>
          </Reveal>
        </div>
      </section>

      {/* My Approach */}
      <section className="px-6 md:px-12 py-16 md:py-24 bg-gradient-to-br from-cove-bluestone/25 via-cove-restorative to-cove-blossom/40" data-testid="about-approach">
        <div className="mx-auto max-w-[1200px]">
          <Reveal>
            <p className="text-xs tracking-[0.25em] uppercase text-cove-rose mb-4">My approach</p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[0.95] text-cove-slate max-w-2xl">
              Whole-body <span className="italic text-cove-duskyrose">transformation</span>
            </h2>
          </Reveal>
          <ul className="mt-12 grid md:grid-cols-3 gap-5">
            {ABOUT.approach.map((line, i) => {
              const tints = ["bg-cove-blossom/70", "bg-cove-restorative", "bg-cove-mutedblue/30"];
              return (
                <Reveal key={i} delay={i * 0.08}>
                  <div className={`h-full rounded-[1.5rem] ${tints[i % tints.length]} border border-cove-slate/10 p-7`}>
                    <Sparkles size={26} className="text-cove-gold" aria-hidden="true" />
                    <p className="mt-3 text-cove-olive leading-relaxed">{line}</p>
                  </div>
                </Reveal>
              );
            })}
          </ul>
        </div>
      </section>

      {/* Google Review CTA */}
      <section className="px-6 md:px-12 py-16 md:py-20 bg-cove-petal" data-testid="about-review">
        <div className="mx-auto max-w-3xl">
          <Reveal>
            <div className="rounded-[2rem] border border-cove-slate/15 bg-cove-blush/25 px-8 md:px-12 py-12 text-center">
              <h2 className="font-serif text-3xl md:text-4xl text-cove-slate">
                {ABOUT.reviewCta.heading}
              </h2>
              <p className="mt-5 text-cove-olive leading-relaxed">{ABOUT.reviewCta.body}</p>
              <div className="mt-8">
                <a
                  href={SITE.googleReviewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="about-google-review-button"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-cove-slate px-8 py-3.5 text-sm font-medium tracking-wide text-cove-cream transition-colors hover:bg-cove-olive"
                >
                  Leave a Google review
                </a>
              </div>
              <p className="mt-6 font-script text-2xl text-cove-rose">{ABOUT.reviewCta.signoff}</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Closing band */}
      <section className="px-6 md:px-12 py-16 md:py-20 bg-cove-petal text-center grain" data-testid="about-closing">
        <div className="mx-auto max-w-3xl">
          <Reveal>
            <p className="text-lg md:text-xl tracking-[0.15em] uppercase text-cove-slate">Your body holds the wisdom.</p>
            <p className="mt-3 font-script text-3xl md:text-5xl text-cove-rose">my role is simply to help you listen.</p>
            <div className="mt-8">
              <CoveButton calendly variant="primary" testId="about-closing-book" className="tracking-[0.2em] uppercase text-xs px-10 py-4">
                Let&rsquo;s Begin Your Healing Journey
              </CoveButton>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
